import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import lsUtil from './lsUtil';
import { strings, getCurrentLocale } from '../../localization';
import { mapboxToken } from '../mapbox/mapboxHelper';
import { encodeLL } from './gpsDecoder';
import axios from 'axios';

import moment from 'moment';

const MAX_UNIX_TS = Math.pow(2,31) - 1;

const DATE_OPTIONS_MEDIUM_NO_YEAR = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
}

const DATE_OPTIONS_MEDIUM_WITH_YEAR = {
    ...DATE_OPTIONS_MEDIUM_NO_YEAR,
    year: 'numeric'
};

const DATE_OPTIONS_TIME_ONLY = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric"
};

const PREFERRED_LOCATION_FEATURE_PLACETYPES = { // Mapbox Doc regarding result prioritization: https://docs.mapbox.com/help/how-mapbox-works/geocoding/#result-prioritization-in-reverse-geocoding
    // primary is considered the most accurate
    primary : [ // breaking up into primary, secondary to simplify logic when using it (basically, if !primary, fallback to secondary sort of thing)
        // ordered most preferred to least preferred
        "address",      // a point feature, e.g. "Maclaren Street" (should also have "address" property "441")
        "poi",          // a point feature, seems to be returned when when no "address", mapbox seems to provide a points-of-interest as the next most accurate place_type (e.g. "255 Consumers Rd. Parking Lot")
        "neighborhood", // a feature that contains the point, e.g. "Centretown"
    ],
    // secondary is more general-area-level
    // currently, getLocationName() uses both secondary placetypes, if available (e.g. "Ottawa, Ontario")
    secondary : [
        "place",        // e.g. "Ottawa"
        "region"        // e.g. "Ontario"
    ]
};

export const BAD_INSTALLATION_STATES = {
    DETECTED: "BAD_INSTALLATION_DETECTED",
    CLEARED: "BAD_INSTALLATION_CLEARED",
    NONE: undefined
};

export default class RavenUtil {

    //gets the currently selected raven from the list of ravens on account.
    static getSelectedRaven = (ravens = null) => {

        if (!ravens) return null;

        var targetRavenUuid = lsUtil.getItem('raven.selectedRavenUuid');

        //return target raven if it exists, otherwise fall through
        var selectedRaven = ravens.find((raven, index) => {
            return raven.ravenUuid === targetRavenUuid && raven.accessLevel !== "INVITATION";
        });
        if (selectedRaven)
            return selectedRaven;

        //get first raven that isn't an invitation
        //(ravens you've been invited to show as invitation but we don't have permission to view their data yet)
        selectedRaven = ravens.find((raven, index) => {
            return raven.accessLevel !== "INVITATION";
        });
        return selectedRaven
    }

    static sortRoute(route) {

        return route.sort(function (elem, elem2) {
            return new Date(elem.ts) - new Date(elem2.ts);
        });
    }

    static toDate(ts) {
        if (!ts) return ts;
        if (ts instanceof Date) return ts;
        if (Number.isInteger(ts)) {
            return ts < MAX_UNIX_TS ? new Date(ts * 1000) : new Date(ts);
        }
        if (typeof ts === 'string' && !ts.endsWith('Z')) {
            ts = ts + 'Z';
        }
        return new Date(ts);
    }

    static convertTs(ts) {

        var newTs = ts;
        if (typeof newTs === "string" && newTs.indexOf('.000Z') === -1) {
            newTs = newTs.replace(' ', 'T');
            newTs += ".000Z";
        }
        // console.log('%c originalDate: ' + ts + '      (' + moment(ts).format("LLL") + ')\nconvertedDate: ' + newTs + ' (' + moment(newTs).format("LLL") + ')', 'color:#888');
        return newTs;
    }

    /**
     * Return a string representation of the provided date.
     * Only the time will be returned for today's date.
     * The 4-digit year will be omitted for the current year.
     * Returns the same argument back if not a Date object.
     **/
    static toDateString(date) {
        const currentDate = new Date();
        const currentLocale = getCurrentLocale();
        if(date instanceof Date) {
            if(date.getUTCFullYear() === currentDate.getUTCFullYear()) {
                if (date.getUTCMonth() === currentDate.getUTCMonth()
                    && date.getUTCDate() === currentDate.getUTCDate()) {
                    return date.toLocaleString(currentLocale, DATE_OPTIONS_TIME_ONLY);
                }
                return date.toLocaleString(currentLocale, DATE_OPTIONS_MEDIUM_NO_YEAR);
            } else {
                return date.toLocaleString(currentLocale, DATE_OPTIONS_MEDIUM_WITH_YEAR);
            }
        }
        return date;
    }

    // Return a number rounded to the given decimal place (1 = 1 decimal place)
    static round(num, precision)
    {
        const factor = Math.pow(10, precision);
        return Math.round(num * factor) / factor;
    }

    /* Convert from km to user units
            units === 1 or METRIC - km
            units !== 1 or IMPERIAL - m
    */
    static convertDistance(km, units)
    {
        if(units === 1)
        {
            return km;
        }
        else if(units === "METRIC")
        {
            return km;
        }
        return km * (1/1.609344);
    }

    static distanceString(distanceInKm, unitOfMeasure, decimals) {
        if (isNaN(decimals)) decimals = 1;
        const PRECISION_FACTOR = Math.pow(10, decimals);
        const converted = Math.round(RavenUtil.convertDistance(distanceInKm, unitOfMeasure) * PRECISION_FACTOR) / PRECISION_FACTOR;
        const distanceUnits = (unitOfMeasure === "METRIC") ? 'km' : 'mi';
        return `${converted.toFixed(decimals)} ${distanceUnits}`;
    }

    static convertArea(km2, units)
    {
        if(units === 1)
        {
            return km2;
        }
        else if(units === "METRIC")
        {
            return km2;
        }
        return km2 * 0.38;
    }

    static areaString(areaInKm, unitOfMeasure, decimals) {
        if (isNaN(decimals)) decimals = 1;
        const PRECISION_FACTOR = Math.pow(10, decimals);
        const converted = Math.round(RavenUtil.convertArea(areaInKm, unitOfMeasure) * PRECISION_FACTOR) / PRECISION_FACTOR;
        const distanceUnits = (unitOfMeasure === "METRIC") ? 'km²' : 'mi²';
        return `${converted.toFixed(decimals)} ${distanceUnits}`;
    }

    static sortEvents(events) {

        events = events.filter(document.filterEvent); // filter out unsupported events
        let clusterEventsKeyCounter = 0; // used for making unique reducerKeys for EVENT_KLOUD_HISTORICAL_STREAMING to avoid collapsing
        const mediaStreamingArchiveIds = []; // used below to keep track of media archive item ids
        events = Object.values( // collapse events with multiple media (legacy card query)
            events.reduce((acc, { media, ...event }) => {
                //const date = new Date(event.ts);
                //const eventId = date.toISOString() + event.type;
                const date = this.toDate(event.ts);
                const eventId = `${date.toJSON()}${event.type}`;
                const mediaIsFormattedAlready = Array.isArray(media);
                let reducerKey = eventId;
                if (media) { // media items may be null (I observed a null media item from a EVENT_IDLING_STARTED event)
                    if (event.type.substring(12) === "HISTORICAL_STREAMING") { // prevent this type of event's duplicates (w/ same timestamp) from being collapsed

                        if (mediaIsFormattedAlready) { // media appears to be formatted correctly already (for example, sortEvents is being triggered during a live update from MQTT)
                            clusterEventsKeyCounter += 1; // keep reducerKey as is, move on to next event's media
                        } else if (media && media.type) {
                            if (media.type === "MEDIA_STREAMING_ARCHIVE") {
                                if (acc[reducerKey] &&
                                    acc[reducerKey].media.length > 0) {
                                    // check if acc[reducerKey].media already has a MEDIA_STREAMING_ARCHIVE item
                                    let hasStreamingArchive = false;
                                    acc[reducerKey].media.forEach(mediaItem => {
                                        if (mediaStreamingArchiveIds.includes(mediaItem.mediaId)) {
                                            hasStreamingArchive = true;
                                        }
                                    });
                                    mediaStreamingArchiveIds.push(media.mediaId); // track this archive id for subsequent checks
                                    if (hasStreamingArchive) {
                                        clusterEventsKeyCounter += 1; // start a new cluster item
                                        reducerKey += clusterEventsKeyCounter; // treat as a unique event
                                    }
                                }
                            }
                        }
                    }
                } /* else {
                    media is null
                }*/
                if (acc[reducerKey]) {
                    if (mediaIsFormattedAlready) {
                        acc[reducerKey].media = media;
                    } else {
                        acc[reducerKey].media.push(media);
                    }
                } else {
                    if (mediaIsFormattedAlready) {
                        acc[reducerKey] = { // clone the event with media as an array
                            ...event,
                            media: media,
                            date,
                            eventId
                        };
                    } else {
                        acc[reducerKey] = { // clone the event with media as an array
                            ...event,
                            media: media && media.mediaId ? [media] : [],
                            date,
                            eventId
                        };
                    }
                }
                return acc;
            }, {})
        );
        events = events.map(event => { // transform eventParams from [{key:'key1', value:'value1'}] to {key1:'value1'}
            const { eventParams } = event;
            if (eventParams && eventParams.length) {
                return {
                    ...event,
                    eventParams: eventParams.reduce((acc, it) => {
                        acc[it.key] = it.value;
                        return acc;
                    }, {})
                };
            }
            return event;
        });
        events.sort((e1,e2) => e2.date - e1.date); // sort events reverse-chronologically
        events = this.markParkedSegments(events);
        events = this.mergeSpeedingEvents(events);
        events = this.markHistoricalStreamingEventsClusters(events);
        events = this.mergeIdlingEvents(events);
        return events;
    }

    static markParkedSegments(events) {

        var parked = false;
        for (var i = 0; i < events.length; i++) {
            if (events[i].type && events[i].type === "EVENT_ENGINE_ON") {
                parked = true;
            }
            events[i].parked = parked;
            if (events[i].type && events[i].type === "EVENT_ENGINE_OFF") {
                parked = false;
            }
        }

        return events;
    }

    static mergeSpeedingEvents(events) {

        if (!events) {
            return events;
        }

        // Instead of merging on STOPPED, reversing loop order and merging on STARTED

        // events are in reverse chron (for now), therefore loop through in reverse order
        for (var i = events.length - 1; i >= 0; i--) {

            if (events[i].type && events[i].type === "EVENT_SPEEDING_STARTED") { // user has started to speed

                for (var j = i - 1; j >= 0; j--) { // continue through events (also in reverse-chron order)

                    if (events[j].type && events[j].type === "EVENT_SPEEDING_STOPPED") {

                        if (j === i - 1) { // only merge if EVENT_SPEEDING_STOPPED is adjacent to EVENT_SPEEDING_STARTED
                            events[i].type = "EVENT_SPEEDING_MERGED";

                            // remove the EVENT_SPEEDING_STOPPED event after merge
                            events.splice(j,1); // https://codepen.io/rrw/pen/wZJpWb
                        }

                        break;

                    } else {

                        events[j].speeding = true; // this event occurred while speeding
                    }
                }
            }
        }

        return events;
    }

    static mergeIdlingEvents(events) {

        if (!events) {
            return events;
        }

        // Instead of merging on STOPPED, reversing loop order and merging on STARTED

        // events are in reverse chron (for now), therefore loop through in reverse order
        for (var i = events.length - 1; i >= 0; i--) {

            if (events[i].type && events[i].type === "EVENT_IDLING_STARTED") { // user has started to speed

                for (var j = i - 1; j >= 0; j--) { // continue through events (also in reverse-chron order)

                    if (events[j].type && events[j].type === "EVENT_IDLING_STOPPED") {

                        if (j === i - 1) { // only merge if EVENT_IDLING_STOPPED is adjacent to EVENT_IDLING_STARTED
                            events[i].type = "EVENT_IDLING_MERGED";
                            events[i].duration = events[j].duration; // merged events should use the idling 

                            // remove the EVENT_IDLING_STOPPED event after merge
                            events.splice(j,1); // https://codepen.io/rrw/pen/wZJpWb
                        }

                        break;

                    } else if (events[j].type && events[j].type === "EVENT_ENGINE_OFF") { // engine off event is before idling stopped (probably due to same timestamp, occurred at the same instant)

                        // find the subsequent event for idling stopped
                        for (var k = j - 1; k >= 0; k--) { // continue through events (also in reverse-chron order)
                            if (events[k].type && events[k].type === "EVENT_IDLING_STOPPED") {
                                events[i].type = "EVENT_IDLING_MERGED";
                                events[i].duration = events[k].duration; // merged events should use the idling 

                                // remove the EVENT_IDLING_STOPPED event after merge
                                events.splice(k,1);
        
                                break;
                            }
                            if (events[k].type && events[k].type === "EVENT_IDLING_STARTED") { // another idling started event before the previous one stopped

                                console.error("Missing EVENT_IDLING_STOPPED event");
                                break;
                            }
                            if (k === 0) { // EVENT_IDLING_STOPPED has not arrived yet

                                events[i].type = "EVENT_IDLING_MERGED";
                                console.log("EVENT_IDLING_STOPPED event has not been reported yet (");
                            }
                        }
                        break;

                    } else {
                        events[j].idling = true; // this event occurred while idling
                    }
                }
            }
        }
        return events;
    }

    static markHistoricalStreamingEventsClusters(events) {

        if (!events) {
            return events;
        }

        for (let i = 0; i < events.length; i += 1) {

            if (events[i].type && events[i].type === "EVENT_KLOUD_HISTORICAL_STREAMING") {

                let historicalStreamingClusterSize = 1;
                const clusterTimestamp = events[i].ts;
                for (var j = i + 1; j < events.length; j += 1) { // continue through events (also in reverse-chron order)
                    if (events[j].type && events[j].type === "EVENT_KLOUD_HISTORICAL_STREAMING") {
                        if (events[j].ts === clusterTimestamp) {
                            historicalStreamingClusterSize += 1;
                            events[j].clustered = true;
                            events[j].clusterOffsetIndex = j - i; // N'th event in this cluster
                        } else {
                            break; // adjacent clusterable event(s), this cluster is finished
                        }
                    } else {
                        break; // cluster finished
                    }
                }
                if (historicalStreamingClusterSize > 1) {
                    events[i].clustered = true;
                    events[i].clusterSize = historicalStreamingClusterSize; // clusterSize-1 is index for the last event in this cluster
                    events[i].clusterOffsetIndex = 0; // first event in this cluster
                }
                // skip to the last event in this cluster before the loop increments i
                i += historicalStreamingClusterSize - 1; // converting cluster size to the index offset of the last clustered item, or an offset of zero if not clustered
            }
        }
        return events;
    }

    static updateHeading(raven = null, route) {

        try {
            var heading = 0;
            var routeToUse = raven && raven.dashboard ? raven.dashboard.route : route;
            if (!routeToUse) {
                raven.vehicleState = { heading: 0 };
                return raven;
            }
            if (raven === null) raven = {
                vehicleState: {
                    heading: 0
                }
            };

            var lastPoint = routeToUse.last();
            var lastUpdatedPoint = routeToUse[routeToUse.length - 2];
            if (raven && raven.vehicleState && raven.vehicleState.indexOfHeadingUpdatePoint) lastUpdatedPoint = routeToUse[raven.vehicleState.indexOfHeadingUpdatePoint];

            if (lastPoint && lastUpdatedPoint) {
                //set to new heading if farther than a magic number in either direction
                //this is an attempt to ignore gps sway. Works better than just checking linear distance (eg. vector length/magnitude)
                var distance = [lastPoint.lon - lastUpdatedPoint.lon, lastPoint.lat - lastUpdatedPoint.lat];
                if (Math.abs(distance[0]) >= 0.0000045 ||
                    Math.abs(distance[1]) >= 0.0000045) {
                    //get heading and add 20% to lon to account for apparent heading offset.
                    heading = Math.atan2((lastPoint.lon - lastUpdatedPoint.lon), (lastPoint.lat - lastUpdatedPoint.lat) * 1.2);
                    heading = (heading) * 180 / Math.PI;
                    if (raven && raven.vehicleState) {
                        raven.vehicleState.indexOfHeadingUpdatePoint = routeToUse.length - 2;
                        raven.vehicleState.heading = heading;
                    }
                    return raven;
                }
            } else {
                return raven;
            }
        } catch (e) {
            //fail elegantly, just pass 0 which is facing north.
            console.warn('unable to get heading', e);
            // var oldHeading = this.map.getSource('car-marker')._data.properties.heading;
            return raven;
        }
        return raven;
    }

    static getDriverNameForEvent (card, event) {

        if (!card) {
            console.warn("RavenUtil getDriverNameForEvent request with invalid card. Returning undefined.", card, event);
            return;
        }

        if (card.type !== "TRIP") {
            console.warn("RavenUtil getDriverNameForEvent request with invalid card type. Returning undefined.", card, event);
            return;
        }

        if (!event) {
            console.warn("RavenUtil getDriverNameForEvent request with invalid event. Returning undefined.", card, event);
            return;
        }

        // check for event params (from mqttService)
        if (event.eventParams) {
            const eventParamDriverName = event.eventParams.EVENT_PARAM_DRIVER_NAME;
            if (eventParamDriverName) {
                    return eventParamDriverName;
            }
        }

        // if not in event params, then try to match against the card's drivers array, if present.
        var eventStartTimeGMTString = typeof event.ts === 'string' ? event.ts : event.ts.toISOString(); // e.g. either Date from MQTT or ISO-8601 (without time zone info)

        if (!eventStartTimeGMTString) {
            console.error("RavenUtil getDriverNameForEvent request event with invalid ts property. Returning undefined.", card, event);
            return;
        }

        if (!eventStartTimeGMTString.endsWith('Z')) {
            eventStartTimeGMTString = eventStartTimeGMTString + 'Z';
        }

        if ((!card.drivers) || (card.drivers.length === 0)) {
            console.log("RavenUtil getDriverNameForEvent request card does not have any drivers associated with it. Returning undefined.", card, event);
            return;
        }
        const driver = card.drivers.find(driver => {
            if (!driver || !driver.startTime) {
                return false;
            }
            var driverStartTime = driver.startTime;
            if (!driverStartTime.endsWith('Z')) {
                driverStartTime = driverStartTime + 'Z';
            }
            return driverStartTime === eventStartTimeGMTString;
        })

        if (driver) {
            if (driver.name) {
                if (driver.name !== "") {
                   return driver.name;
                }
            }
        }
        return;
    }

    static getSpeedzoneForEvent (card, event) {

        if (!card) {
            console.warn("RavenUtil getSpeedzoneForEvent request with invalid card. Returning undefined. Card/event:", card, event);
            return;
        }

        if (card.type !== "TRIP") {
            console.warn("RavenUtil getSpeedzoneForEvent request with invalid card type. Returning undefined. Card/event:", card, event);
            return;
        }

        if (!event) {
            console.warn("RavenUtil getSpeedzoneForEvent request with invalid event. Returning undefined. Card/event:", card, event);
            return;
        }

        const eventStartTimeString = event.ts; // e.g. "ts":"2019-03-01T23:07:12"

        if (!eventStartTimeString) {
            console.error("RavenUtil getSpeedzoneForEvent request event with invalid ts property. Returning undefined. Card/event:", card, event);
            return;
        }

        if ((!card.segments) || (card.segments.length === 0)) {
            console.warn("RavenUtil getSpeedzoneForEvent request card invalid/empty segments array property. Returning undefined. Card/event:", card, event);
            return;
        }

        var speedZone;
        for (var i = 0; i < card.segments.length; i++) {
            if ((!card.segments[i].speedZones) || (card.segments[i].speedZones.length === 0)) {
                continue; // no speeding reported during this segment
            }
            speedZone = card.segments[i].speedZones.find((speedZone) => speedZone.startTime === eventStartTimeString);
            if (speedZone) {
                break;
            }
        }
        return speedZone;
    }

    static getLocationString(reverseGeocodeFeatures, isDriving) {

        try {
            var locationString = null;
            if (reverseGeocodeFeatures) {
                //default - this or POI are returned when available.
                var address = reverseGeocodeFeatures.find(feature => {
                    return feature.place_type.indexOf('address') >= 0;
                })
                //could be this instead of address
                var poi = reverseGeocodeFeatures.find(feature => {
                    return feature.place_type.indexOf('poi') >= 0;
                })
                //backup for default
                var neighborhood = reverseGeocodeFeatures.find(feature => {
                    return feature.place_type.indexOf('neighborhood') >= 0;
                })
                //finally, this is used everywhere or as at least something when the above don't exist
                var place = reverseGeocodeFeatures.find(feature => {
                    return feature.place_type.indexOf('place') >= 0;
                })

                //make the location text
                if (address) {

                    locationString = (address.address ? address.address + " " : "") + address.text;
                    if (isDriving) locationString = address.text;
                } else if (poi) {

                    locationString = poi.properties.address;
                    //requirement was to have no street number when car is moving (!parked)
                    //can't guarantee format of poi address, so leaving as default (full street address). eg. returned as 487 University Lane, not as address: 487, text: University Lane
                    // - so won't try to parse just street name out - don't know if it will have unit number, other random characters, how many spaces before st. name etc.
                    // if (dbType !== "PARKED") locationString = poi.properties.address;
                } else if (neighborhood) {

                    locationString = neighborhood.text;
                }

                if (place) {
                    if (locationString)
                        locationString += ", " + place.text;
                    else
                        locationString = place.text;
                }

                if (!locationString) {

                    console.error("reverseGeocodeFeatures didn't contain address, poi or neighborhood data", reverseGeocodeFeatures);
                    locationString = strings.formatString(strings.db_unknown_location);
                }
            }

            return locationString;
        } catch (e) {
            //means address wasn't valid for some reason and this should be fixed because a property on some location feature didn't exist when it was expected.
            console.error(e, reverseGeocodeFeatures);
        }
    }

    //a function which informs you of the parked state of the car.
    //accounts for the dashboard type as well as all the edge cases for being parked mid-trip
    static getParkedTime(dashboard) {

        if (!dashboard) return null;

        var parkedTime = null;
        if (dashboard.events) {
            //if parked but was driving
            for (var i = 0; i < dashboard.events.length; i++) {
                var e = dashboard.events[i];
                if (e.type.indexOf('ENGINE_ON') > -1) {
                    return null; //don't fall through to dashboard type - the card is likely about to refresh momentarily due to a new trip card beginning
                }
                if (e.type.indexOf('ENGINE_UNKNOWN') > -1 || e.type.indexOf('ENGINE_OFF') > -1) {
                    parkedTime = e.ts;
                    break;
                }
            }
        }

        if (dashboard.type === "PARKED") {
            if (dashboard.start) parkedTime = dashboard.start.ts;
            else if (dashboard.route && dashboard.route.length > 0) parkedTime = dashboard.route.last().ts;
            else parkedTime = new Date();
        }

        return parkedTime;
    }

    static isDriving(raven) {
        if(raven.vehicleState && raven.vehicleState.segId)
        {

            return((raven.vehicleState.segId % 2) == 1);
        }

        return !this.getParkedTime(raven.dashboard);
    }

    static isIdling(dashboard) {

        if (!dashboard) {
            return null; /* state not known */
        }

        if (dashboard.type === "PARKED") {
            return false; /* can't be idling if parked */
        }

        if (!dashboard.events || dashboard.events.length == 0) {
            return false; /* even though there should be at least one event, treat this as no idling events yet */
        }
        for (var i = 0; i < dashboard.events.length; i++) {
            var event = dashboard.events[i];
            if (event.type === "EVENT_IDLING_STOPPED") {
                return false;
            }
            if (event.type === "EVENT_IDLING_STARTED") {
                return true;
            }
        }
        return false;
    }

    static isSuspended (raven) {

        if (!raven) {
            return null; /* state not known */
        }

        if (!raven.status) {
            return false; /* even though there should be at least one event, treat this as no shutdown events yet */
        }

        if (raven.status === "SUSPENDED") {
            return true;
        }

        return false;
    }

    static isShutdownByUser (dashboard) {

        if (!dashboard) {
            return null; /* state not known */
        }

        if (!dashboard.events || dashboard.events.length == 0) {
            return false; /* even though there should be at least one event, treat this as no shutdown events yet */
        }

        switch (dashboard.events[0].type) {
        case "EVENT_RAVEN_SHUTDOWN":
        case "EVENT_RAVEN_USER_SHUTDOWN":
            return true;
        default:
            return false;
        }
    }

    static getBadInstallState (dashboard) {

        if (!dashboard) {
            return null; /* state not known */
        }

        if (!dashboard.events || dashboard.events.length == 0) {
            return false; /* even though there should be at least one event, treat this as no bad install events yet */
        }

        // find the most recent bad install event
        for (let i = 0; i < dashboard.events.length; i++) {
            var event = dashboard.events[i];
            switch (event.type) {
            case "EVENT_RAVEN_BAD_INSTALL_DETECTED":
                return BAD_INSTALLATION_STATES.DETECTED;
            case "EVENT_RAVEN_BAD_INSTALL_CLEARED":
                return BAD_INSTALLATION_STATES.CLEARED;
            }
        }

    };

    static isShutDownForLowBattery (dashboard) {
        if (!dashboard) {
            return null; /* state not known */
        }
        if (!dashboard.events || dashboard.events.length == 0) {
            return false; /* even though there should be at least one event, treat this as no shutdown events yet */
        }

        for (let i = 0; i < dashboard.events.length; i++) {
            var event = dashboard.events[i];
            if (event.type === "EVENT_ENGINE_ON") { // not shut down, therefore can't be shut down due to low battery
                return false;
            }
            if (event.type === "EVENT_CAR_BATTERY_LOW") {
                return true;
            }
        }
        return false;
    }

    //check if a raven has recieved an update in the last 5 seconds
    //unused but leaving logic in case it's needed later
    static isLive(raven) {

        if (new Date() - raven.queryCompleteTime <= 5000) return true; //if query was completed less than xx seconds ago - querycomplete time will always exist

        if (raven.dashboard && raven.dashboard.route && raven.dashboard.route.length > 0) {
            if (raven.dashboard.route.last().ts) {
                var lastTime = new Date(RavenUtil.convertTs(raven.dashboard.route.last().ts))
                if (lastTime) {
                    var timeFromNow = new Date().getTime() - lastTime.getTime();
                    if (timeFromNow <= 5000) {
                        return true
                    }
                }
            }
        }
        return false;
    }

    static getPreviewImageHTML(raven, cameraid, width, height) {
        var showImage =
            raven.liveMedia &&
            raven.liveMedia[cameraid] &&
            raven.liveMedia[cameraid].imageData &&
            raven.liveMedia[cameraid].imageData.length > 0;
        var imageElement = null;
        var timestampLabel = null;

        if (showImage) {
            try {
                var media = raven.liveMedia[cameraid];
                imageElement = <img src={media.base64Image} width={width} height={height} />;
                timestampLabel = <div className="timestamp-label">{moment(RavenUtil.convertTs(raven.liveMedia[cameraid].timestamp)).format('MMMM Do[,] h:mm a')}</div>;
            } catch (e) {
                console.warn(e);
            }
        } else if (raven && raven.vehicleState && raven.vehicleState.currentPreviewUrls) {

            try {
                var media = raven.vehicleState.currentPreviewUrls[cameraid];
                if(media)
                {
                    imageElement = <img src={media.finalUrl} width={width} height={height} />;
                    timestampLabel = <div className="timestamp-label">{moment(RavenUtil.convertTs(raven.vehicleState.currentPreviewUrls[cameraid].ts)).format('MMMM Do[,] h:mm a')}</div>;
                }
            } catch (e) {
                console.warn(e);
            }
        }

        return {
            image: imageElement,
            timestamp: timestampLabel,
        }
    }

    //:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::
    //:::                                                                         :::
    //:::  This routine calculates the distance between two points (given the     :::
    //:::  latitude/longitude of those points). It is being used to calculate     :::
    //:::  the distance between two locations using GeoDataSource (TM) prodducts  :::
    //:::                                                                         :::
    //:::  Definitions:                                                           :::
    //:::    South latitudes are negative, east longitudes are positive           :::
    //:::                                                                         :::
    //:::  Passed to function:                                                    :::
    //:::    lat1, lon1 = Latitude and Longitude of point 1 (in decimal degrees)  :::
    //:::    lat2, lon2 = Latitude and Longitude of point 2 (in decimal degrees)  :::
    //:::    unit = the unit you desire for results                               :::
    //:::           where: 'M' is statute miles (default)                         :::
    //:::                  'K' is kilometers                                      :::
    //:::                  'N' is nautical miles                                  :::
    //:::                                                                         :::
    //:::  Worldwide cities and other features databases with latitude longitude  :::
    //:::  are available at https://www.geodatasource.com                          :::
    //:::                                                                         :::
    //:::  For enquiries, please contact sales@geodatasource.com                  :::
    //:::                                                                         :::
    //:::  Official Web site: https://www.geodatasource.com                        :::
    //:::                                                                         :::
    //:::               GeoDataSource.com (C) All Rights Reserved 2017            :::
    //:::                                                                         :::
    //:::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::::

    static distance(lat1, lon1, lat2, lon2, unit) {
        var radlat1 = Math.PI * lat1 / 180
        var radlat2 = Math.PI * lat2 / 180
        var theta = lon1 - lon2
        var radtheta = Math.PI * theta / 180
        var dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
        if (dist > 1) {
            dist = 1;
        }
        dist = Math.acos(dist)
        dist = dist * 180 / Math.PI
        dist = dist * 60 * 1.1515
        if (unit == "K") { dist = dist * 1.609344 }
        if (unit == "N") { dist = dist * 0.8684 }
        return dist
    }

    static routeDistanceInKm(route, dontRound = false) {

        var totalDistance = 0;
        if (Array.isArray(route) && route.length > 1) {

            for (var i = 1; i < route.length; i++) {
                var p1 = route[i - 1];
                var p2 = route[i];
                totalDistance += RavenUtil.distance(p1.lat, p1.lon, p2.lat, p2.lon, 'K');
            }

            if (dontRound) return totalDistance

            return Math.round(totalDistance * 10) / 10; //round to 1 decimal place
        } else return totalDistance
    }

    static getLocationFeatures(lon, lat, callback, errorCallback, signal = undefined) {

        if(!lon || !lat)
        {
            // What are you doing to me?  Sending me NULL?

            setTimeout(() => { 
                errorCallback({
                    response: {
                        data: "NULL",
                        status: 404,
                    },
                });
            }, 0);

            return;
        }

        var dlon = lon < 0 ?
                encodeLL(-lon, 'W') :
                encodeLL(lon, 'E');
        var dlat = lat < 0 ?
                encodeLL(-lat, 'S') :
                encodeLL(lat, 'N');

        if(!RavenUtil.locationCache)
        {
            RavenUtil.locationCache = {};
        }

        var key = "" + dlon + ',' + dlat;

        if(RavenUtil.locationCache[key])
        {
            setTimeout(() => { callback(RavenUtil.locationCache[key]) }, 0);
        }

        var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' + lon + ',' + lat + '.json?access_token=' + mapboxToken;

        const options = {
            method: 'get',
            url: url,
        }

        if(signal) {
            options.signal = signal;
        }

        axios(options)
        .then((response) => {
            RavenUtil.locationCache[key] = response;
            callback(response);
        }).catch(errorCallback);
    }

    static getLocationName (locationFeatures, addressOnly = false) {
        if (!locationFeatures.data) {
            console.warn("ravenUtil invalid locationFeatures, missing data");
            return;
        } else if (!locationFeatures.data.features) {
            console.warn("ravenUtil invalid locationFeatures, missing features");
            return;
        } else if (locationFeatures.data.features.length <= 0) {
            console.warn("ravenUtil invalid locationFeatures, empty features");
            return;
        }

        var preferredPrimaryLocationNameSource;

        for (const placeTypeString of PREFERRED_LOCATION_FEATURE_PLACETYPES.primary) { // using for loop to have 'break' option

            // search features for place_types in order of preference
            preferredPrimaryLocationNameSource = locationFeatures.data.features.find(feature => {
                if (feature.place_type && feature.place_type.length > 0) {
                    if (feature.place_type[0] === placeTypeString) {
                        return true;
                    }
                }
                return false;
            });

            if (preferredPrimaryLocationNameSource) {
                break; // preferred source found!
            }
        }

        var locationName; // get text property for PREFERRED_LOCATION_FEATURE_PLACETYPES.primary
        if (preferredPrimaryLocationNameSource && preferredPrimaryLocationNameSource.text) {
            
            locationName = preferredPrimaryLocationNameSource.text; // default is just the text value

            if (preferredPrimaryLocationNameSource.properties && preferredPrimaryLocationNameSource.properties.address) {
                locationName = locationName + ", " + preferredPrimaryLocationNameSource.properties.address;
            } else if (preferredPrimaryLocationNameSource.address) { // for feature with place_type includes "address", there is a top level property "address" which is the street number
                locationName = preferredPrimaryLocationNameSource.address + " " + locationName;
            }
        }

        if (addressOnly) {
            return locationName || undefined;
        }

        var generalAreaName; // will be a CSV of PREFERRED_LOCATION_FEATURE_PLACETYPES.secondary's, if available (e.g. "Ottawa, Ontario")
        locationFeatures.data.features.forEach(feature => {
            if (PREFERRED_LOCATION_FEATURE_PLACETYPES.secondary.includes(feature.place_type[0])) { // regarding using "place_type" first array item: https://docs.mapbox.com/api/search/#geocoding-response-object
                generalAreaName = generalAreaName? generalAreaName + ", " + feature.text : feature.text;
            }
        });

        if (generalAreaName) {
            return locationName ? locationName + ", " + generalAreaName : generalAreaName;
        }

        return locationName; // if !generalAreaName, probably returning undefined
    }

    static userHasOwnerAccessLevel(ravens) {

        if (!ravens || ravens.length === 0) {
            //console.warn("SettingsPage render+getHighestRavenAccessLevel while ravens undefined");
            return undefined;
        }

        for (var i = 0; i < ravens.length; i += 1) { // using basic for loop so I can return on getHighestRavenAccessLevel immediately
            if (!ravens[i].hasOwnProperty("accessLevel")) {
                console.error("SettingsPage getHighestRavenAccessLevel raven missing accessLevel property");
                continue;
            }
            if (ravens[i].accessLevel === "OWNER") {
                return true;
            }
        }
        return false;
    }

}
